/**
 * Recipe API Service
 * Handles all recipe-related API calls to the backend
 */

import { Recipe, RecipeRecord } from '../types/recipe'
import { BaseApiService } from './BaseApiService'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3005'

class RecipeApiService extends BaseApiService {
  constructor() {
    super({
      baseUrl: API_BASE_URL,
      defaultTimeout: 15000,
      defaultRetries: 3,
      enableRetry: true
    })
  }
  /**
   * Save recipes to database
   */
  async saveRecipes(userId: string, sessionId: string, recipes: Recipe[]): Promise<RecipeRecord[]> {
    const data = await this.post<{ data: RecipeRecord[] }>(
      `/api/users/${userId}/recipes`,
      { sessionId, recipes }
    )
    return data.data
  }

  /**
   * Get all recipes for a user's session
   */
  async getUserSessionRecipes(userId: string, sessionId: string): Promise<RecipeRecord[]> {
    const data = await this.get<{ recipes: RecipeRecord[] }>(
      `/api/users/${userId}/sessions/${sessionId}/recipes`
    )
    return data.recipes
  }

  /**
   * Update recipe assigned dates
   */
  async updateRecipeAssignedDates(recipeId: string, dates: string[]): Promise<RecipeRecord> {
    const data = await this.put<{ data: RecipeRecord }>(
      `/api/recipes/${recipeId}/dates`,
      { dates }
    )
    return data.data
  }

  /**
   * Mark recipe as cooked/not cooked
   */
  async markRecipeAsCooked(recipeId: string, isCooked: boolean): Promise<RecipeRecord> {
    const data = await this.put<{ data: RecipeRecord }>(
      `/api/recipes/${recipeId}/cooked`,
      { isCooked }
    )
    return data.data
  }

  /**
   * Delete a recipe (user dismissed it without cooking)
   */
  async deleteRecipe(recipeId: string): Promise<void> {
    await this.delete(`/api/recipes/${recipeId}`)
  }

  /**
   * Get recipes by date range
   */
  async getRecipesByDateRange(userId: string, startDate: string, endDate?: string): Promise<RecipeRecord[]> {
    let endpoint = `/api/users/${userId}/recipes/dates?startDate=${startDate}`
    if (endDate) {
      endpoint += `&endDate=${endDate}`
    }

    const data = await this.get<{ recipes: RecipeRecord[] }>(endpoint)
    return data.recipes
  }

  /**
   * Get unassigned recipes from a specific session (for calendar recipe bank)
   */
  async getUnassignedRecipesBySession(userId: string, sessionId: string): Promise<RecipeRecord[]> {
    const data = await this.get<{ recipes: RecipeRecord[] }>(
      `/api/users/${userId}/sessions/${sessionId}/recipes/unassigned`
    )
    return data.recipes
  }

  /**
   * Get unassigned recipes from sessions created since a specific date
   * Used to populate calendar recipe bank with recent recipes from last 7 days
   */
  async getUnassignedRecipesSince(userId: string, sinceDate: string): Promise<RecipeRecord[]> {
    const data = await this.get<{ recipes: RecipeRecord[] }>(
      `/api/users/${userId}/recipes/unassigned-since/${sinceDate}`
    )
    return data.recipes
  }

  /**
   * Get all recipes for a user (for history view)
   */
  async getAllUserRecipes(userId: string, filters?: { mealType?: string }): Promise<RecipeRecord[]> {
    let endpoint = `/api/users/${userId}/recipes`
    if (filters?.mealType) {
      endpoint += `?mealType=${filters.mealType}`
    }

    const data = await this.get<{ recipes: RecipeRecord[] }>(endpoint)
    return data.recipes
  }
}

export const recipeApiService = new RecipeApiService()
export default recipeApiService
